import { IconDefinition } from "@fortawesome/fontawesome-svg-core"
import { faXmarkLarge } from "@fortawesome/pro-light-svg-icons"
import { faFileZipper, faFilm, faImage } from "@fortawesome/pro-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, IconButton, Tooltip, Typography } from "@mui/material"
import { alpha } from "@mui/system"
import { getAdvertiserNameById } from "advertiser/tools/common"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { CAMPAIGN_STATUSES, ICampaign } from "campaign/model"
import { campaignRPO } from "campaign/repository"
import { FORM_TABS } from "form/model"
import { ICampaignMediaCreative, IMedia } from "media/model"
import { mediaRPO } from "media/repository"
import { deleteCampaignMedia, getDisplayMediaFormat, getMediaFormat } from "media/tools/common"
import { getCreativeTypeFromMimeType } from "media/tools/media-type"
import { getCreativeStatusBackgroundColor } from "media/tools/status"
import { getOrderNameById } from "order/tools/common"
import { NotFoundPage } from "page/components/NotFoundPage"
import { UnexpectedErrorPage } from "page/components/UnexpectedErrorPage"
import { CAMPAIGN_EDIT_PATH, CAMPAIGN_TAB_ID_QUERY_PARAM, FORECAST_EDIT_PATH, NOT_FOUND_PATH } from "page/model"
import { navigate } from "page/tools/navigate"
import React from "react"
import { connect } from "react-redux"
import { useParams } from "react-router-dom"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { isPhoneScreen } from "ui/tools/responsive"
import { CreativeStatus } from "./CreativeStatus"
import { CreativeUploadPopup } from "./CreativeUploadPopup"
import { MediaCreativeDisplay } from "./MediaCreativeDisplay"

interface ICampaignMediaPageProps {
  isPhone: boolean
}

interface ISimpleInfoLine {
  type: string
  value?: string | null
  icon?: IconDefinition
}

interface IInfoLine {
  type: string
  name: string
  id: number | null
}

type Errors = "unexpected" | "not-found" | "no-creative" | "not-found-campaign"

const SimpleInfoLine = React.memo(({ value, type, icon }: ISimpleInfoLine) => (
  <Typography
    sx={{ "color": theme => theme.palette.text.primary2, "& .icon": { fontSize: "1rem", ml: "1rem", mr: "-.5rem" } }}>
    <Typography color="textPrimary" fontSize=".75rem" component="span">
      {type}:
    </Typography>
    {icon && (
      <FontAwesomeIcon
        icon={type === "image" ? faImage : type === "video" ? faFilm : faFileZipper}
        size="1x"
        className="icon"
      />
    )}
    <Typography fontSize=".75rem" component="span" sx={{ ml: "1rem" }}>
      {value ? value : "-"}
    </Typography>
  </Typography>
))

const InfoLine = React.memo(({ name, id, type }: IInfoLine) => (
  <FlexBox ai="start" vertical sx={{ ml: "1rem", maxWidth: "100%" }}>
    <Tooltip title={name ? name : "-"}>
      <Typography>
        <Typography fontSize=".75rem" color="textPrimary" component="span">
          {type}:
        </Typography>
        <Typography
          fontSize=".75rem"
          sx={{
            ml: ".5rem",
            color: theme => theme.palette.text.primary2,
            maxWidth: "100%",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis"
          }}
          component="span">
          {name ? name : "-"}
        </Typography>
      </Typography>
    </Tooltip>
    <Typography fontSize=".75rem" sx={{ color: theme => theme.palette.text.secondary }}>
      ID: {id !== null ? id : "-"}
    </Typography>
  </FlexBox>
))

const CampaignMediaPageFn = React.memo(({ isPhone }: ICampaignMediaPageProps): JSX.Element => {
  const [media, setMedia] = React.useState<IMedia | null>(null)
  const [isUploadPopupOpened, setIsUploadPopupOpened] = React.useState(false)
  const [campaign, setCampaign] = React.useState<ICampaign | null>(null)
  const [creative, setCreative] = React.useState<ICampaignMediaCreative | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<"" | Errors>("")
  const { campaignId, mediaId } = useParams()
  const areParamsIncorrect = campaignId === "" || isNaN(campaignId as any) || mediaId === "" || isNaN(mediaId as any)
  React.useEffect(() => {
    if ((media !== null && campaign !== null) || isLoading || error) {
      return
    }
    setIsLoading(true)
    let processedRequestCount = 0
    const toProcessRequest = 2
    const onFinish = (err?: Errors) => {
      if (err) {
        setError(err)
        setIsLoading(false)
      } else if (++processedRequestCount === toProcessRequest) {
        setIsLoading(false)
      }
    }
    mediaRPO
      .getCampaignMedia(Number(campaignId), Number(mediaId))
      .then(fetchedMedia => {
        if (fetchedMedia.creatives.length === 0) {
          onFinish("no-creative")
          return
        }
        setMedia(fetchedMedia)
        setCreative(fetchedMedia.creatives[0])
        onFinish()
      })
      .catch(err => {
        console.error(err)
        onFinish(err === NOT_FOUND_PATH ? "not-found" : "unexpected")
      })
    campaignRPO
      .getCampaign(Number(campaignId))
      .then(fetchedCampaign => {
        setCampaign(fetchedCampaign)
        onFinish()
      })
      .catch(err => {
        console.error(err)
        onFinish(err === NOT_FOUND_PATH ? "not-found-campaign" : "unexpected")
      })
  })
  const type = creative ? getCreativeTypeFromMimeType(creative.mime_type) : "image"
  if (error === "not-found" || error === "not-found-campaign" || areParamsIncorrect) {
    return (
      <NotFoundPage
        message={translate(areParamsIncorrect ? "media.creative.error.not-found" : `media.creative.error.${error}`)}
      />
    )
  } else if (error) {
    return <UnexpectedErrorPage message={translate(`media.creative.error.${error}`)} />
  }
  const mediaFormat = media ? getMediaFormat(media) : undefined
  const statusBackgroundColor =
    media && media.approval_status_id ? getCreativeStatusBackgroundColor(media.approval_status_id) : "white"
  const redirectToForm = () => {
    if (!campaign) {
      return
    }
    navigate({
      path: campaign.status_id === CAMPAIGN_STATUSES.PLANNING ? FORECAST_EDIT_PATH : CAMPAIGN_EDIT_PATH,
      pathParams: campaign.id,
      hashParams: [[CAMPAIGN_TAB_ID_QUERY_PARAM, FORM_TABS.CREATIVES]]
    })
  }
  return (
    <FlexBox vertical fullWidth fullHeight jc="start">
      {isLoading || !media || !creative || !campaign ? (
        <LinearLoader text={translate("media.creative.loading")} sx={{ mt: "2rem", maxWidth: "calc(100% - 6rem)" }} />
      ) : (
        <>
          <FlexBox
            fullWidth
            vertical
            gap=".5rem"
            jc="start"
            sx={{
              "position": "relative",
              "height": isPhone ? "50%" : "calc(100% - 21rem)",
              "minHeight": isPhone ? "50%" : "calc(100% - 21rem)",
              "maxHeight": isPhone ? "50%" : "calc(100% - 21rem)",
              "backgroundColor": theme => alpha(theme.palette.text.primary2 as any, 0.15),
              "& > *": {
                width: "calc(100% - 2rem)",
                maxWidth: "50rem"
              }
            }}>
            <FlexBox
              jc="start"
              sx={{
                pl: "1rem",
                mt: "2rem",
                height: "3.5rem",
                border: `1px solid ${statusBackgroundColor}`,
                borderRadius: ".25rem",
                backgroundColor: alpha(statusBackgroundColor, 0.2)
              }}>
              <CreativeStatus
                status={media.approval_status_id}
                refusalNote={media.refusal_note}
                uppercase
                sx={{
                  "& p": {
                    fontSize: "1.2rem"
                  },
                  "& svg.icon": {
                    width: "2rem !important",
                    height: "2rem !important",
                    mr: "1rem"
                  }
                }}
              />
            </FlexBox>
            <FlexBox
              jc="start"
              sx={{
                height: "calc(100% - 10rem)",
                minHeight: "calc(100% - 10rem)",
                maxHeight: "calc(100% - 10rem)",
                mt: ".5rem",
                mb: "3.5rem",
                backgroundColor: (theme: any) => theme.palette.background.default,
                overflow: "hidden"
              }}>
              <MediaCreativeDisplay
                type={type}
                mediaId={media.id}
                creativeId={creative.id}
                sx={{
                  height: "calc(100% - 7rem)",
                  minHeight: "calc(100% - 7rem)",
                  maxHeight: "calc(100% - 7rem)"
                }}
              />
            </FlexBox>
            <IconButton
              sx={{ width: "2.5rem", position: "absolute", top: "2rem", right: "2rem" }}
              onClick={() => redirectToForm()}>
              <FontAwesomeIcon icon={faXmarkLarge} />
            </IconButton>
          </FlexBox>
          <FlexBox
            gap
            wrap={isPhone}
            sx={{
              "mt": "1rem",
              "width": "calc(100% - 2rem)",
              "maxWidth": "50rem",
              "& > *": {
                width: isPhone ? "100%" : "49%",
                maxWidth: isPhone ? "100%" : "49%"
              },
              "& .info-panel": {
                gap: ".5rem",
                justifyContent: "start",
                pl: "1rem",
                mt: "1rem",
                borderLeft: theme => theme.palette.border,
                borderWidth: "2px",
                height: "10rem"
              }
            }}>
            <FlexBox vertical ai="start" className="info-panel">
              <Tooltip title={creative.name || ""}>
                <Typography
                  fontSize="1.5rem"
                  fontWeight={550}
                  sx={{
                    color: theme => theme.palette.text.primary2,
                    maxWidth: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis"
                  }}>
                  {creative.name}
                </Typography>
              </Tooltip>
              <Typography
                fontSize=".75rem"
                fontWeight={500}
                sx={{ mt: "-.6rem", color: theme => theme.palette.text.secondary }}>
                ID: {creative.id}
              </Typography>
              <SimpleInfoLine type={translate("label.format")} value={getDisplayMediaFormat(media)} />
              <SimpleInfoLine
                type={translate("label.type")}
                value={translate(`campaign.creative.row.type.${type}`)}
                icon={type === "image" ? faImage : type === "video" ? faFilm : faFileZipper}
              />
            </FlexBox>
            {!isPhone && (
              <FlexBox ai="start" vertical className="info-panel">
                <Typography fontSize=".75rem" fontWeight={500} color="textPrimary">
                  {translate("campaign.creative.connect.to")}
                </Typography>
                <InfoLine type={translate("campaign.title")} name={campaign.product || ""} id={campaign.id} />
                <InfoLine
                  type={translate("label.order")}
                  name={campaign.order_id !== null ? getOrderNameById(campaign.order_id) : ""}
                  id={campaign.order_id}
                />
                <InfoLine
                  type={translate("label.advertiser")}
                  name={campaign.advertiser_id ? getAdvertiserNameById(campaign.advertiser_id) : ""}
                  id={campaign.advertiser_id}
                />
              </FlexBox>
            )}
          </FlexBox>
          <FlexBox gap jc="end" sx={{ mt: "2rem", width: "calc(100% - 2rem)", maxWidth: "50rem" }}>
            <Button variant="text" onClick={() => redirectToForm()}>
              {translate("campaign.creative.replace.later")}
            </Button>
            <Button variant="contained" onClick={() => setIsUploadPopupOpened(true)}>
              {translate("campaign.creative.replace")}
            </Button>
          </FlexBox>
          {isUploadPopupOpened && (
            <CreativeUploadPopup
              params={{
                campaignId: campaign.id,
                formatId: media.format_id,
                width: mediaFormat ? mediaFormat[0] : media.width,
                height: mediaFormat ? mediaFormat[1] : media.height,
                isVideo: type === "video"
              }}
              onUploadSuccess={async () => {
                try {
                  await deleteCampaignMedia(campaign.id, media.campaignMediaId as number)
                  redirectToForm()
                } catch (err) {
                  console.error(err)
                  setError("unexpected")
                }
              }}
              onClose={() => setIsUploadPopupOpened(false)}
            />
          )}
        </>
      )}
    </FlexBox>
  )
})

export const CampaignMediaPage = connect(
  (state: IRootState): ICampaignMediaPageProps => ({
    isPhone: isPhoneScreen(state)
  })
)(CampaignMediaPageFn)
